import axios from "axios";

export const handleTranslate = async ({
    id,
    locale: targetLocale,
    formData,
    handleChange,
    setTranslationLoading,
    setErrorMessage,
    setOpenSnackbar,
    setSeverity,
    fieldType,
    overwrite = false
}) => {
    const sourceValue = formData[id]?.['en'];
    const LIST_TYPES = ['numbered-list', 'bulleted-list']

    if (!sourceValue) {
        setErrorMessage("No English text available for translation");
        setOpenSnackbar(true);
        setSeverity("warning");
        return;
    }

    setTranslationLoading(prev => ({
        ...prev,
        [`${id}-${targetLocale}`]: true
    }));

    try {
        let translatedValue;

        if (fieldType === 'richText') {
            if (!Array.isArray(sourceValue)) {
                throw new Error("Invalid richText format");
            }

            // Process each node recursively while maintaining structure
            const processNode = async (node) => {
                // Create a new node with the same structure
                const newNode = { ...node };

                // Handle text nodes
                if (node.text && node.text.trim()) {
                    try {
                        // Preserve whitespace around text (Links)
                        const leadingSpace = node.text.match(/^\s+/)?.[0] || '';
                        const trailingSpace = node.text.match(/\s+$/)?.[0] || '';
                        const textToTranslate = node.text.trim();

                        if (textToTranslate) {
                            const response = await axios.post(
                                `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_TRANSLATION_API_KEY}`,
                                {
                                    q: textToTranslate,
                                    source: 'en',
                                    target: targetLocale,
                                    format: 'text'
                                },
                                {
                                    headers: {
                                        'Content-Type': 'application/json',
                                    }
                                }
                            );
                            const translatedText = response?.data?.data?.translations?.[0]?.translatedText || textToTranslate;
                            newNode.text = leadingSpace + translatedText + trailingSpace;
                        }
                    } catch (error) {
                        console.warn('Translation failed for text node:', node.text);
                        newNode.text = node.text; // Fallback to original text
                    }
                }

                // Handle nodes with children (recursively)
                if (node.children) {
                    newNode.children = await Promise.all(
                        node.children.map(child => processNode(child))
                    );
                }

                return newNode;
            };

            // Process all top-level nodes while maintaining exact structure
            translatedValue = await Promise.all(
                sourceValue.map(node => processNode(node))
            );

            // Ensure we maintain the exact same node count and types
            if (translatedValue.length !== sourceValue.length) {
                console.warn('Node count mismatch after translation. Adjusting...');
                // Pad with empty paragraphs if needed
                while (translatedValue.length < sourceValue.length) {
                    translatedValue.push({
                        type: 'paragraph',
                        children: [{ text: '' }]
                    });
                }
                // Trim if in any case its longer
                translatedValue = translatedValue.slice(0, sourceValue.length);
            }
            const existingText = Array.isArray(formData[id]?.[targetLocale])
                ? formData[id][targetLocale]
                : [];

            // Process translated content
            translatedValue = translatedValue.map((node, i) => ({
                ...node,
                type: sourceValue[i]?.type || 'paragraph'
            }));

            // Add paragraph before each list item to avoid non-leaf issue
            translatedValue = translatedValue.flatMap(node => {
                if (LIST_TYPES.includes(node.type)) {
                    return [
                        { type: 'paragraph', children: [{ text: '' }] },
                        node
                    ];
                }
                return node;
            });

            // // Merge with existing content
            // translatedValue = existingText.length > 0
            //     ? [...existingText, ...translatedValue]
            //     : translatedValue;

        } else if (Array.isArray(sourceValue) && fieldType === 'array_string') {
            // This is an array of strings to translate
            try {
                const translatedArray = await Promise.all(
                    sourceValue.map(async (text) => {
                        if (!text?.trim()) return text; // Skip empty strings

                        const leadingSpace = text.match(/^\s+/)?.[0] || '';
                        const trailingSpace = text.match(/\s+$/)?.[0] || '';
                        const trimmed = text.trim();

                        try {
                            const response = await axios.post(
                                `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_TRANSLATION_API_KEY}`,
                                {
                                    q: trimmed,
                                    source: 'en',
                                    target: targetLocale,
                                    format: 'text'
                                },
                                {
                                    headers: {
                                        'Content-Type': 'application/json',
                                    }
                                }
                            );
                            const translated = response?.data?.data?.translations?.[0]?.translatedText || trimmed;
                            return leadingSpace + translated + trailingSpace;
                        } catch (err) {
                            console.warn('Failed to translate array item:', text);
                            return text;
                        }
                    })
                );

                translatedValue = translatedArray;

            } catch (err) {
                console.error('Error translating array:', err);
                translatedValue = sourceValue; // fallback
            }
        } else {
            // Handle regular text fields
            const response = await axios.post(
                `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_TRANSLATION_API_KEY}`,
                {
                    q: sourceValue,
                    source: 'en',
                    target: targetLocale,
                    format: 'text'
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            const translatedText = response.data.data.translations[0].translatedText;
            const existingText = formData[id]?.[targetLocale] || '';

            translatedValue = (!overwrite && existingText)
                ? `${existingText}\n${translatedText}`
                : translatedText;
        }

        // Update form state
        handleChange(id, translatedValue, targetLocale);

    } catch (error) {
        console.error('Translation error:', error);
        setErrorMessage(error.response?.data?.error?.message || "Translation failed");
        setOpenSnackbar(true);
        setSeverity("error");

        // Return original value on error to maintain stability
        return formData[id]?.[targetLocale] || sourceValue;
    } finally {
        setTranslationLoading(prev => ({
            ...prev,
            [`${id}-${targetLocale}`]: false
        }));
    }
};